import { all, call, take, takeLatest, put, select, takeEvery, actionChannel, fork, cancel, delay, debounce, flush } from 'redux-saga/effects';
import { store } from 'store/store';
import { eventChannel, buffers } from 'redux-saga';
import { transactionActions, getGroupKey, resolveTransactionId } from '../transactionsSlice';
import * as aiqConfig from 'config';
import * as transactionSelectors from 'store/transactionsSlice/transactionsSelectors';
import { desksActions } from 'store/desksSlice/desksSlice';
import { snackbarActions } from 'store/snackbarsSlice/snackbarsSlice';
import { tableControlActions } from 'store/tableControlSlice/tableControlSlice';
import { formControlActions } from 'store/formControlSlice/formControlSlice';
import { transactionImageActions } from 'store/storeTransactionImage/storeTransactionImageSlice';
import { getTransactionImages } from 'services/transactionImagesService';
import { MACAW_IMAGE_STATUS } from 'utils/macawImages';
import { notificationHandler, transformCameraConfiguration } from 'utils/utils';
import { resolveRelayEnforcementFromRelaysConfig } from 'utils/relayEnforcement';
import {
    seedVideoWallPinnedLanes,
    handleStickyLaneUnpinnedDeskMessage,
    resetStickyLaneHydration,
    resetLaneAot,
    clearPinnedLanesLocally,
    resyncPinnedLanesOnReconnect,
} from './stickyLaneSagas';
import {
    createExternalAnnouncementsSelector,
    selectFormsObject,
    selectIsTransactionInProcessingStateById,
} from '../../formControlSlice/formControlSelectors';
import { selectSubStreamCameraIds, selectTransactions } from '../transactionsSelectors';
import { selectAllTablesData } from '../../tableControlSlice/tableControlSelectors';
import { cameraStreamManager } from 'services/CameraStream/CameraStreamManager';
import { withUsableSubStreams } from 'utils/transactionSubStream';
import { selectUser } from 'store/authSlice/authSelectors';
import { selectIsActiveDeskConfirmed, selectCurrentDesk, selectExpectedFinishedTransactionMillis } from 'store/desksSlice/desksSelectors';
import { sendLocalStorageSignal } from 'utils/sessionStorageHandlers';
import { talkManager } from 'services/Talk/TalkManager';
import { axiosAiq } from 'config';
import { streamsUseReducerDispatch } from 'hooks/useStreams/useStreams';
import { TRANSACTION_PAGE_TABS } from 'config';
import { displayStateToFlags, displayStateFromFlags, VIDEO_WALL_HANDOFF_STORAGE_KEY } from 'utils/videoWall/displayState';
import { isVideoWallTransactionActive } from 'utils/videoWall/laneNotifications';
import { vinCheckPageStateUpdateFns } from 'pages/VinCheck';
import { initializeNewForm } from 'store/formControlSlice/formControlSagas';
import { isDeskAvailable } from 'store/desksSlice/desksSagas';
import {
    sharedWorkerActions,
    sharedWorker as sharedWorkerObj,
    sharedWorkerMessageType as messageType,
    stompTopics,
} from 'workers/sharedWorker/utils/utils';
import getSharedWorker from '../../../workers/sharedWorker/utils/sharedWorker-factory';
import { startSharedWorkerHeartbeat } from 'services/SharedWorkerLiveness/sharedWorkerLiveness';
import { perf } from '../utils/performance';
import { WS_CONNECTION_STATE } from 'config';
import transactionManager from '../utils/TransactionChannelManager';
import ChannelBuffer from '../utils/ChannelBuffer';
import { convertObjectKeysToCamelCase } from 'utils/convertObjectKeysToCamelCase';
import { fenixYmsActions } from 'store/fenixYmsSlice/fenixYmsSlice';
import { slackActions } from 'store/slackSlice/slackSlice';
import { SLACK_SEND_RESULT_MESSAGE_TYPE, parseSlackSendResultMessage } from 'utils/slackMessages';
import { aiqLogger } from 'services/aiqLogger';
import { getNowFormattedDate } from 'utils/getNowFormatedDate';
import { kurentoClientManager } from 'services/CameraStream/KurentoRtspSource/KurentoClient/KurentoClientManager';
import { webRtcEndpointRegistry } from 'services/CameraStream/KurentoRtspSource/WebRtcEndpointRegistry';
import {
    VIDEO_WALL_FINISHED_TRANSACTION_MEMORY_MS,
    VIDEO_WALL_NOTIFICATION_FADE_MS,
    VIDEO_WALL_NOTIFICATION_TIMEOUT_MS,
} from '../transactionConstants';
import { ACTIVITY_END_REASON } from 'utils/activityTracking/constants';
import { getTruckTransactionStatus } from 'services/transactionService';

const finishedTransactionIdsQueue = [];
let transactionIdsMarkedForDropping = [];
export { VIDEO_WALL_NOTIFICATION_FADE_MS, VIDEO_WALL_NOTIFICATION_TIMEOUT_MS } from '../transactionConstants';
import { VIDEO_WALL_CONFIG_PUBLISH_DEBOUNCE_MS } from '../transactionConstants';

const allDesksTopic = '/topic/all-desks';

const {
    DESKS_TAB: { ID: desksTabId },
    REOPENED_TRANSACTION_TAB: { ID: reopenedTransactionTabId },
    VIDEO_WALL_TAB: { ID: videoWallTabId, DUMMY_TRANSACTION_ID: videoWallTransactionId },
} = TRANSACTION_PAGE_TABS;

let stompBrokerTopics = {};

// Video-wall notifications are keyed by lane (gateId) so every camera of a lane - primary and auxiliary -
// shares one notification. Keyed by gateId here too (timeout ids per lane).
const videoWallTransactions = new Map();

// Lane key for a raw video-wall STOMP message. Deliberately NO gate_camera_id fallback: gate.id and
// gate_camera.id are independent sequences, so falling back would let a message for gate_camera 42 collide
// with - and clear - the notification of the unrelated lane 42. A fallback would also be useless: the UI
// resolves notifications via getLaneNotification(), which reads gateId only, so anything stored under a
// gate_camera_id key renders on no tile. Every producer emits gate_id; a message without one is dropped.
const resolveVideoWallLaneKey = (rawMessage) => rawMessage?.gate_id;

function removeByNestedValue(obj, targetValue) {
    for (const key in obj) {
        if (typeof obj[key] === 'object' && obj[key] !== null) {
            if (obj[key]?.value?.toUpperCase() === targetValue) {
                delete obj[key];
            } else {
                removeByNestedValue(obj[key], targetValue);
            }
        }
    }
    return obj;
}

// A lane notification must never outlive the truck. Both producers of a notification - the detection message
// and an assignment that had to create the lane entry itself - arm the same per-lane timer (keyed by gateId in
// videoWallTransactions). When it runs out, handleVideoWallNotificationTimeout asks the backend which of the lane's
// transactions are still open, so a lane whose finish never reached this client clears instead of staying locked.
const armVideoWallNotificationTimeout = (laneKey, payload) => {
    const previousTimeoutId = videoWallTransactions.get(laneKey);
    previousTimeoutId && clearTimeout(previousTimeoutId);

    const timeoutId = setTimeout(() => {
        videoWallTransactions.delete(laneKey);
        store.dispatch(transactionActions.videoWallNotificationTimedOut(payload));
    }, VIDEO_WALL_NOTIFICATION_TIMEOUT_MS);
    videoWallTransactions.set(laneKey, timeoutId);
};

const TRANSACTION_UNKNOWN_TO_BACKEND = 'UNKNOWN';
// What the backend answers for an id it has no transaction for (INVALID_ID / NOT_FOUND)
const UNKNOWN_TRANSACTION_HTTP_STATUSES = [400, 404];

// Null when the status could not be read this time (network, timeout, server error, auth, rate limit): the lane is
// kept and checked again later, as freeing it would show an open transaction's lane as free. A transaction the backend
// does not know is not coming back, so it counts as ended and its lane still clears.
function* fetchTruckTransactionStatus(truckTransactionId) {
    try {
        return yield call(getTruckTransactionStatus, truckTransactionId);
    } catch (error) {
        if (UNKNOWN_TRANSACTION_HTTP_STATUSES.includes(error?.response?.status)) return TRANSACTION_UNKNOWN_TO_BACKEND;
        console.warn(`handleVideoWallNotificationTimeout(): could not read transaction ${truckTransactionId}, checking again later`, error);
        return null;
    }
}

// The timeout only stands in for a finish this client never received, it is no limit on how long a truck may take.
// A lane is kept while the backend still has one of its transactions STARTED: dropping it showed an open transaction's
// lane as free on a quiet lane and invited another agent to take the same truck (BEAST-13807).
export function* handleVideoWallNotificationTimeout({ payload }) {
    const { gateId } = payload;
    const lane = (yield select(transactionSelectors.selectVideoWallTransactionNotifications))?.[gateId];
    if (!lane || lane.finishedAt) return;

    const checkedIds = (lane.transactions ?? []).map(({ truckTransactionId }) => String(truckTransactionId));
    const statuses = yield all(checkedIds.map((truckTransactionId) => call(fetchTruckTransactionStatus, truckTransactionId)));
    const endedIds = checkedIds.filter((_, index) => statuses[index] != null && statuses[index] !== 'STARTED');

    // Messages keep landing during the requests: a lane that started fading has its own removal timer, and a
    // transaction that arrived meanwhile is live. A detection meanwhile re-armed the timer but was ignored by the
    // reducer (the lane was live), so the lane now stands for that truck.
    const currentLane = (yield select(transactionSelectors.selectVideoWallTransactionNotifications))?.[gateId];
    if (!currentLane || currentLane.finishedAt) return;
    const hasDetectionArrived = videoWallTransactions.has(gateId);
    const remaining = (currentLane.transactions ?? []).filter(({ truckTransactionId }) => !endedIds.includes(String(truckTransactionId)));

    // A late resend must not bring an ended transaction back once its entry is gone.
    for (const truckTransactionId of endedIds) yield put(transactionActions.rememberFinishedVideoWallTransaction(truckTransactionId));

    if (!remaining.length && !hasDetectionArrived) {
        yield put(transactionActions.removeVideoWallTransactionNotification({ ...payload, forceNotificationRemove: true }));
        return;
    }
    // The tile renders transactions[0], so an ended entry kept next to an open one would show its agent and timer.
    if (endedIds.length) {
        yield put(transactionActions.dropVideoWallLaneTransactions({ gateId, truckTransactionIds: endedIds, receivedAt: Date.now() }));
    }
    if (!hasDetectionArrived) armVideoWallNotificationTimeout(gateId, payload);
}

// This message is the only source of a lane's assignee, and a transaction can be assigned more than once:
// returning it to queue hands it to another desk, which publishes this again with the new agent.
//
// Two cases need the timer armed here rather than by the detection message. The lane may have no notification
// at all (a wall opened mid-transaction, or a lane whose notification already timed out) - the reducer then
// builds one from this payload so the assignment is not simply dropped. Or the lane may be mid-fade with a
// NEW transaction arriving, in which case the reducer clears finishedAt and the fade's own removal timer no
// longer applies. A late message for the transaction that just finished is a third case, and is dropped.
export function* handleVideoWallSubmittedAgentInformationMessage(data) {
    const gateId = resolveVideoWallLaneKey(data);
    if (gateId == null) {
        console.warn('handleVideoWallSubmittedAgentInformationMessage(): ignoring message without gate_id', data);
        return false;
    }

    // eslint-disable-next-line
    const { ['@c']: _, ...detectionInfo } = data;
    const payload = convertObjectKeysToCamelCase(detectionInfo);

    // A late resend for a transaction that already ended. After the fade it would rebuild the lane with that agent
    // and a fresh timer that no status message ever clears again.
    const finishedTransactionIds = yield select(transactionSelectors.selectVideoWallFinishedTransactionIds);
    if (finishedTransactionIds[payload.truckTransactionId]) {
        console.log(`handleVideoWallSubmittedAgentInformationMessage(): truckTransactionId=${payload.truckTransactionId} already finished, ignored`);
        return false;
    }

    const notifications = yield select(transactionSelectors.selectVideoWallTransactionNotifications);
    const notification = notifications?.[gateId];
    const holdsTransaction = (notification?.transactions ?? []).some(
        (transaction) => String(transaction.truckTransactionId) === String(payload.truckTransactionId)
    );

    // A fading lane plus an assignment for a transaction it already holds is a late message for the
    // transaction that just finished (see the matching guard in updateVideoWallTransactionNotification).
    // Bail before the timer too: re-arming would replace the fade's 5s removal with the full notification
    // timeout and leave a submitted transaction's tile on the wall for minutes.
    if (notification?.finishedAt && holdsTransaction) return false;

    yield put(transactionActions.updateVideoWallTransactionNotification(payload));

    // The reducer either built the lane or cleared its finishedAt. Either way the lane is left with no
    // removal timer that still applies, so arm one.
    if (!notification || notification.finishedAt) armVideoWallNotificationTimeout(gateId, payload);

    return true;
}

export function* handleTransactionStatusVideoWallMessage(data) {
    const gateId = resolveVideoWallLaneKey(data);
    if (gateId == null) {
        console.warn('handleTransactionStatusVideoWallMessage(): ignoring message without gate_id', data);
        return false;
    }
    const truckTransactionId = data?.truck_transaction_id ?? data?.truckTransactionId;
    // Prefer the server timestamp. Date.now() is only a legacy fallback and can theoretically collide
    // when two status messages for the same lane arrive within the same millisecond.
    const finishedAt = data?.finishedAt ?? data?.finished_at ?? Date.now();
    // Remembered even when this wall has no lane for it: a later assignment for it is still late.
    if (truckTransactionId != null) yield put(transactionActions.rememberFinishedVideoWallTransaction(truckTransactionId));
    const notifications = yield select(transactionSelectors.selectVideoWallTransactionNotifications);
    const notification = notifications?.[gateId];
    if (!notification) return false;

    const transactions = notification.transactions ?? [];
    const hasMatchingTransaction = transactions.some((transaction) => String(transaction.truckTransactionId) === String(truckTransactionId));
    if (transactions.length && !hasMatchingTransaction) return false;

    const remainingTransactions = transactions.filter((transaction) => String(transaction.truckTransactionId) !== String(truckTransactionId));
    if (remainingTransactions.length) {
        yield put(transactionActions.removeVideoWallTransactionNotification({ gateId, truckTransactionId }));
        return true;
    }

    yield put(transactionActions.completeVideoWallTransactionNotification({ gateId, finishedAt }));

    const timeoutId = videoWallTransactions.get(gateId);
    timeoutId && clearTimeout(timeoutId);

    const removalTimeoutId = setTimeout(() => {
        videoWallTransactions.delete(gateId);
        store.dispatch(
            transactionActions.removeVideoWallTransactionNotification({
                gateId,
                forceNotificationRemove: true,
                expectedFinishedAt: finishedAt,
            })
        );
    }, VIDEO_WALL_NOTIFICATION_FADE_MS);
    videoWallTransactions.set(gateId, removalTimeoutId);

    return true;
}

export function* handleStompBrokerResponse(payload) {
    const command = payload.command;

    switch (command) {
        case 'MESSAGE':
            yield call(handleStompBrokerMessage, payload);
            break;

        default:
            console.log('PAYLOAD MISSED SWITCH STATMENT IN TRANSACTIONS SAGA', payload.command);
    }
}

//TODO remove stomp broker creation and message handling into new file/class

// Whether this tab is subscribed to any STOMP topic, i.e. depends on the broker connection (transaction page,
// video wall, VIN check...). Unsubscribed topics are kept as `false`, hence the value check.
export function hasStompSubscriptions() {
    return Object.values(stompBrokerTopics).some(Boolean);
}

export function* handleStompConnectionStateChange(data) {
    const previousConnectionState = yield select(transactionSelectors.selectStompBrokerConnectionState);
    yield put(transactionActions.setStompBrokerConnectionState(data));
    switch (data) {
        case WS_CONNECTION_STATE.CONNECTING:
            yield call(handleWebSocketError);
            yield call(clearPinnedLanesLocally);
            break;
        case WS_CONNECTION_STATE.DISCONNECTED: {
            // A tab without subscriptions (e.g. desk configuration) lost nothing and is not force-reloaded by the
            // worker, so it is not told to reload either: that would throw away its unsaved edits.
            const dependsOnStomp = yield call(hasStompSubscriptions);
            if (dependsOnStomp) yield call(handleError);
            yield call(clearPinnedLanesLocally);
            break;
        }
        case WS_CONNECTION_STATE.CONNECTED:
            if (previousConnectionState === WS_CONNECTION_STATE.DISCONNECTED || previousConnectionState === WS_CONNECTION_STATE.CONNECTING) {
                yield call(resyncPinnedLanesOnReconnect);
                // image status messages sent while disconnected are lost
                yield call(refreshOpenTransactionsImages);
            }
            break;
    }
}

///handleStompBrokerResponse utils
export function* handleStompBrokerMessage(payload) {
    let data = JSON.parse(payload.body);
    const topic = payload.headers?.subscription;
    console.log(`handleStompBrokerMessage(): message received topic=${topic}, data=${payload.body}`);

    const { username: activeUser } = yield select(selectUser);

    if (topic === stompTopics.kurentoStatus.topicUrl) {
        const kurentoId = data?.['kurento_id'];
        const active = data?.active;
        if (!kurentoId) return;
        try {
            kurentoClientManager.updateKurentoActiveState(kurentoId, active);
            if (!active) {
                store.dispatch(transactionActions.invalidateCameraConfigByKurentoId(kurentoId));
                cameraStreamManager.handleKurentoInactive(kurentoId);
                streamsUseReducerDispatch.forEach((dispatch) => {
                    dispatch({ type: 'handleKurentoInvalid', payload: kurentoId });
                });
            }
        } catch (err) {
            console.error(`Error handling Kurento active status update, kurentoId=${kurentoId}, message=${err?.message}`);
        }

        return;
    }
    if (topic === allDesksTopic) {
        yield put(desksActions.replaceDesk(data.desk));
        const { id: currentDeskId, name: currentDeskName } = yield select(selectCurrentDesk);
        const isActiveDeskConfirmed = yield select(selectIsActiveDeskConfirmed);
        const incomingDeskId = data?.desk.id;
        const incomingDeskUser = data?.desk?.sessions?.[0]?.account?.username;
        const isReceivingTransactionsEnabled = yield select(transactionSelectors.selectIsReceivingTransactionsEnabled);

        switch (true) {
            case !isActiveDeskConfirmed:
                if (incomingDeskId !== currentDeskId || !incomingDeskUser) return;
                if (incomingDeskUser !== activeUser) {
                    yield call(leaveDesk, false);
                    yield call(notificationHandler, {
                        title: `${currentDeskName.toUpperCase()} is taken by ${incomingDeskUser}, please select different desk.`,
                    });
                }

                yield put(desksActions.setIsActiveDeskConfirmed(true));
                return;

            case incomingDeskId === currentDeskId && !incomingDeskUser && isReceivingTransactionsEnabled:
                yield call(leaveDesk);
                break;
            case incomingDeskId !== currentDeskId && incomingDeskUser === activeUser:
                yield put(desksActions.selectDesk(data.desk));
                break;

            default:
        }

        return;
    }

    if (topic.startsWith('/topic/vin-locations.')) {
        const incomingTransactionId = data?.['vin_transaction_id'];
        vinCheckPageStateUpdateFns.forEach((setProcessingActions) =>
            setProcessingActions((state) => {
                const { transactionId } = state;
                if (incomingTransactionId === transactionId) {
                    return { ...state, isTransactionProcessed: true, requestInProgress: { ...state.requestInProgress, sendingSnapshots: false } };
                }
                return state;
            })
        );
    }

    if (topic === stompTopics.cameraUpdates.topicUrl) {
        const currentDesk = yield select(selectCurrentDesk);
        const activeDeskId = currentDesk?.id;
        if (data['@c'] === 'VideoWallRealTimeUpdateCamerasMessage' && data?.desk_id === activeDeskId) {
            yield call(setRealTimeVideoWallCameras, data);
            return;
        }
        yield call(updateCameraConfig, data, cameraStreamManager.getTransactionIdsByCameraId(data.id));
        return;
    }

    if (data['@c'] === '.DeskCameraStreamMessage') {
        yield call(updateCameraConfig, data.camera, data?.['transaction_ids']);
        return;
    }

    if (data['@c'] === '.DeskAssignedMessage') {
        console.log(
            `handleStompBrokerMessage(): DeskAssignedMessage received by browser tab, transactionId=${data?.truck_transaction_id}, version=${data?.version}`
        );
        perf.mark(data, perf.markNames.received);
        yield put(transactionActions.deskAssignedMessageReceived(data));
    }

    // Result of an async Slack send; the send saga matches it to its request by log_id.
    if (data['@c'] === SLACK_SEND_RESULT_MESSAGE_TYPE) {
        yield put(slackActions.sendResultReceived(parseSlackSendResultMessage(data)));
        return;
    }

    if (data['@c'] === '.TransactionImageStatusMessage') {
        yield put(transactionActions.transactionImageStatusReceived(data));
        return;
    }

    if (data['@c'] === '.AgentReviewDeskMessage') {
        aiqLogger.log(
            { transactionId: data?.truck_transaction_id, outcome: data?.outcome, reason: data?.reason },
            'handleStompBrokerMessage_AgentReviewDeskMessage'
        );
        yield put(transactionActions.agentReviewMessageReceived(data));
        return;
    }

    if (data['@c'] === '.DeskAotMessage') yield call(handleDeskAotMessage, data);

    if (data['@c'] === '.VideoWallNewTransactionMessage') {
        // eslint-disable-next-line
        const { ['@c']: _, ...detection } = data;
        // The full body is already logged on receipt (handleStompBrokerMessage), so only the identifiers here.
        console.log(
            `handleStompBrokerMessage(): VideoWallNewTransactionMessage received gateId=${data?.gate_id}, transactionId=${data?.truck_transaction_id ?? data?.gate_transaction_id}`
        );

        const laneKey = resolveVideoWallLaneKey(data);
        if (laneKey == null) {
            console.warn('handleStompBrokerMessage(): ignoring VideoWallNewTransactionMessage without gate_id', data);
            return;
        }

        // A lane being processed ignores the detection, and must keep its timer too: the timer is what checks a
        // transaction whose finish never reached this wall, and on a lane where trucks keep coming it would never run
        // (BEAST-13740). No timer pending means the lane's check is running, and the timer armed here tells it a truck
        // is waiting.
        const lane = (yield select(transactionSelectors.selectVideoWallTransactionNotifications))?.[laneKey];
        if (!isVideoWallTransactionActive(lane) || !videoWallTransactions.has(laneKey)) {
            armVideoWallNotificationTimeout(laneKey, convertObjectKeysToCamelCase(detection));
        }
        // receivedAt is this browser's clock, unlike the server-side timestamp, so the Unassigned assign delay
        // cannot be shortened or stretched by clock skew between the browser and the server.
        yield put(transactionActions.addVideoWallTransactionNotification({ ...convertObjectKeysToCamelCase(detection), receivedAt: Date.now() }));
    }

    if (data['@c'] === '.VideoWallSubmittedAgentInformationMessage') {
        yield call(handleVideoWallSubmittedAgentInformationMessage, data);
    }

    if (data['@c'] === '.TransactionStatusVideoWallMessage') {
        yield call(handleTransactionStatusVideoWallMessage, data);
    }

    if (data['@c'] === '.StickyLanePinnedVideoWallMessage') {
        const { gateId, accountUsername, timestamp } = convertObjectKeysToCamelCase(data);
        yield put(transactionActions.addVideoWallPinnedLane({ gateId, accountUsername, pinnedAt: timestamp }));
    }

    if (data['@c'] === '.StickyLaneUnpinnedVideoWallMessage') {
        yield put(transactionActions.removeVideoWallPinnedLane(convertObjectKeysToCamelCase(data).gateId));
    }

    if (data['@c'] === '.StickyLaneUnpinnedDeskMessage') {
        yield call(handleStickyLaneUnpinnedDeskMessage, data);
    }

    if (data['@c'] === '.VideoWallCameraStreamMessage') {
        const camConfig = transformCameraConfiguration(data.camera);
        cameraStreamManager.updateCameraConfig(camConfig).then(() => {
            const transactionIdsArr = cameraStreamManager.getTransactionIdsByCameraId(camConfig.camId);
            store.dispatch(
                transactionActions.setCameraConfig({
                    transactionIdsArr,
                    camConfig,
                    activeOnVideoWall: cameraStreamManager.isActiveOnVideoWall(camConfig.camId),
                })
            );
        });
    }

    if (data['@c'] === '.ExternalAnnouncementMessage') {
        const { transaction_id: transactionId, announcement_source: announcementSource, announcements: incomingAnnouncements } = data || {};

        if (!incomingAnnouncements?.length) return;

        const stateAnnouncements = yield select(createExternalAnnouncementsSelector(transactionId, announcementSource));

        //merge and deduplicate
        const uniqueByInternalId = new Map();
        const announcements = [...structuredClone(stateAnnouncements), ...incomingAnnouncements];

        announcements.forEach((ann) => uniqueByInternalId.set(ann.internalId, ann));

        const mergedAnnouncements = Array.from(uniqueByInternalId.values());

        yield put(formControlActions.setExternalAnnouncements({ formId: transactionId, announcementSource, announcements: mergedAnnouncements }));
    }

    if (data['@c'] === '.SheetAnnouncementMessage') {
        const { status, announcements } = data || {};
        if (!announcements?.length) return;

        const pendingGateToTransaction = yield select(transactionSelectors.selectSheetAnnouncementPendingGate);
        announcements.forEach((announcement) => {
            const transactionId = pendingGateToTransaction?.[announcement?.gateId];
            if (!transactionId) return;
            store.dispatch(transactionActions.setSheetAnnouncements({ ...announcement, transactionId, status }));
        });
    }

    if (data['@c'] === '.YmsExportResponseMessage') {
        const { transactionId, requestId, status, statusMessage, ymsDetails, ymsUrl, responseTimestamp, exportedToYms } =
            convertObjectKeysToCamelCase(data) || {};

        const currentTransaction = yield select(transactionSelectors.selectTranasctionById(transactionId));
        if (!currentTransaction) return;

        yield put(
            fenixYmsActions.exportResponseReceived({
                transactionId,
                requestId,
                status,
                message: statusMessage ?? null,
                details: ymsDetails ?? null,
                url: ymsUrl ?? null,
                timestamp: responseTimestamp ?? null,
                exportedToYms: exportedToYms ?? null,
            })
        );
        return;
    }
}

function* updateCameraConfig(newCameraConfiguration, transactionIdsArr = []) {
    const camConfig = transformCameraConfiguration(newCameraConfiguration);
    if (!camConfig.videoData.streamType) camConfig.videoData.streamType = 'TEST';
    yield cameraStreamManager.updateCameraConfig(camConfig).then(() => {
        transactionIdsArr &&
            transactionIdsArr.length &&
            store.dispatch(
                transactionActions.setCameraConfig({
                    transactionIdsArr,
                    camConfig,
                    activeOnVideoWall: cameraStreamManager.isActiveOnVideoWall(camConfig.camId),
                })
            );

        streamsUseReducerDispatch.length &&
            streamsUseReducerDispatch.forEach((dispatch) => dispatch({ type: 'editStreamConfig', payload: camConfig }));
    });

    return;
}

export function* getSaturationCameras(deskId) {
    try {
        const response = yield axiosAiq(`/locations/desk/${deskId}/cameras/saturation?connect=true`);
        yield call(preconfigureCameraStreams, response.data);
    } catch (err) {
        yield call(notificationHandler, {
            err,
            title: `Error getting saturation cameras`,
        });
    }
}

export function* getVideoWallCameras({ payload }) {
    try {
        yield put(transactionActions.setFetchingVideoWallCamerasInProgress(true));
        const response = yield axiosAiq.get(`/locations/desk/${payload}/cameras/saturation?connect=true`);
        yield call(preconfigureCameraStreams, response.data);
        yield put(transactionActions.setVideoWallCameras(response.data));
    } catch (err) {
        yield call(notificationHandler, {
            err,
            title: `Error getting video wall cameras`,
        });
    }
    yield put(transactionActions.setFetchingVideoWallCamerasInProgress(false));
}

export function* setRealTimeVideoWallCameras(payload) {
    try {
        const isVideoWallEnabled = yield select(transactionSelectors.selectIsVideoWallEnabled);
        const isFetchingVideoWall = yield select(transactionSelectors.selectFetchingVideoWallCamerasInProgress);
        if (isFetchingVideoWall) {
            yield delay(2000);
        }
        yield put(transactionActions.showRealTimeUpdateMessages({ isLoadingRealTimeStreams: isVideoWallEnabled, showRealTimeUpdateMessages: true }));
        const gateCameras = payload.gate_camera_infos.map((info) => info.gate_camera_info);
        const gateCamerasInfo = payload.gate_camera_infos.map((info) => info);
        if (!isVideoWallEnabled) return;
        yield call(preconfigureCameraStreams, gateCameras);
        yield put(transactionActions.setRealTimeVideoWallCameras(gateCamerasInfo));
    } catch (err) {
        yield call(notificationHandler, {
            err,
            title: `Error getting video wall cameras`,
        });
    }
}

async function preconfigureCameraStreams(camConfigList) {
    await (cameraStreamManager.preconfigeCamerasPromise = Promise.allSettled(
        camConfigList.map((camConfig) => cameraStreamManager.create(transformCameraConfiguration(camConfig), true))
    ));
    const camIds = new Set(camConfigList.map((camConfig) => camConfig.id));
    console.log(`preconfigureCameras(): Success preconfigured ${camIds.size} cameras, ids=[${[...camIds]}]`);
    cameraStreamManager.preconfigeCamerasPromise = null;
}

function* toggleVideoWallEnabled({ payload: { enabled, updateSessionStorage = true } }) {
    yield put(transactionActions.setChangingOperationalModeInProgress(true));
    yield put(transactionActions.setIsVideoWallEnabled(enabled));
    updateSessionStorage && sessionStorage.setItem(aiqConfig.sessionStorageKeys.videoWallEnabled, enabled);
    sharedWorkerActions.windowModeChange({ subscribe: enabled, topicName: 'videoWall' });
    const currentDesk = yield select(selectCurrentDesk);
    const activeDeskId = currentDesk?.id;
    if (!activeDeskId) {
        yield put(transactionActions.setChangingOperationalModeInProgress(false));
        return;
    }
    if (!enabled) {
        yield put(transactionActions.unsubscribeFromStompBrokerTopic('videoWall'));
        yield put(transactionActions.removeVideoWallCameras());
        yield cameraStreamManager.stopStreamsForTab(videoWallTabId, videoWallTransactionId);
    }
    if (enabled) {
        yield call(getVideoWallCameras, { payload: activeDeskId });

        yield put(transactionActions.subscribeToStompBrokerTopic({ topic: 'videoWall', url: '/topic/video-wall' }));
        // VW enabled on its own after the desk was taken -> seed the pin overlay from the backend.
        yield call(seedVideoWallPinnedLanes);
    }
    yield put(transactionActions.setChangingOperationalModeInProgress(false));
}

function* toggleIsReceivingTransactionsEnabled({ payload: { enabled, updateSessionStorage = true } }) {
    yield put(transactionActions.setChangingOperationalModeInProgress(true));
    yield put(transactionActions.setIsReceivingTransactionsEnabled(enabled));
    sharedWorkerActions.windowModeChange({ subscribe: enabled, topicName: stompTopics.transactionDesk });
    updateSessionStorage && sessionStorage.setItem(aiqConfig.sessionStorageKeys.receivingTransactionsEnabled, enabled);
    const currentDesk = yield select(selectCurrentDesk);
    const activeDeskId = currentDesk?.id;
    if (!activeDeskId) {
        yield put(transactionActions.setChangingOperationalModeInProgress(false));
        return;
    }

    if (!enabled) {
        yield put(desksActions.setIsActiveDeskConfirmed(true));
        const isVideoWallEnabled = yield select(transactionSelectors.selectIsVideoWallEnabled);
        //TO DO  same logic  as in closeTransactionPanel fn (in case closeAllTabs=true) modify it to reuse same logic (note: keep in mind - closeTransctionPanel has its reducer fn inside store)
        yield put(transactionActions.setActiveTab(isVideoWallEnabled ? videoWallTabId : desksTabId));
        const transacions = yield select(transactionSelectors.selectTransactions);
        for (let transacion of transacions) yield put(formControlActions.removeForm(transacion.id));
        yield put(transactionActions.setTransactions([]));
        yield put(transactionActions.setUpdatingTransactionInProgress({ initialState: true }));
        yield put(transactionActions.setHasReopenedTransaction(false));
        yield put(tableControlActions.setAllTablesData({}));
        //
        yield cameraStreamManager.stopAllStreamsExcludeTab(videoWallTabId);
        yield talkManager.stopSession();
    }
    if (enabled) {
        const { desk, takenBy } = yield call(isDeskAvailable, currentDesk);
        if (desk) {
            yield put(desksActions.setIsDeskLoading(true));
            yield call(getSaturationCameras, desk.id);
            yield call(subscribeToStompBrokerTopic, { payload: { topic: stompTopics.transactionDesk, url: `/topic/desks.${activeDeskId}` } });
            yield put(desksActions.setIsActiveDeskConfirmed(false));
            yield put(desksActions.setIsDeskLoading(false));
        } else {
            yield call(notificationHandler, {
                variant: 'info',
                title: `Transaction mode is not currently available for ${currentDesk.name.toUpperCase()}, it is taken ${
                    takenBy ? ` by ${takenBy}` : ''
                }`,
            });
            yield put(transactionActions.setIsReceivingTransactionsEnabled(false));
        }
    }
    yield put(transactionActions.setChangingOperationalModeInProgress(false));
}

export function* handleDeskAssignedMessage(data) {
    const transactionId = resolveTransactionId(data);
    const locationId = data?.location_id;
    const gateId = data?.gate_id;
    const version = data?.version;
    const reopened = data?.reopened;
    const automation = data?.automation;
    const initNewFormData = { gateId, locationId, companyId: data?.company_id, gateTransactionId: data?.gate_transaction_id };

    const currentTransaction = yield select(transactionSelectors.selectTranasctionById(transactionId));
    const currentVersion = currentTransaction?.version;

    console.log(
        `handleDeskAssignedMessage(): Starting to process transactionId=${transactionId}, currentVersion=${currentVersion}, incomingVersion=${version} `
    );

    const isTransactionInProcessingState = yield select(selectIsTransactionInProcessingStateById(transactionId));
    const allowNewTransactionInitialization =
        !currentTransaction && !isTransactionInProcessingState && (!finishedTransactionIdsQueue.includes(transactionId) || reopened);

    if (allowNewTransactionInitialization) {
        console.log(`handleDeskAssignedMessage(): Initializing new transaction, transactionId=${transactionId}`);
        yield put(transactionActions.handleNewTransaction(data));
        console.log(`handleDeskAssignedMessage(): Initializing form and getting encoded images, transactionId=${transactionId}`);
        perf.mark(data, perf.markNames.initializeNewFormStart);
        yield call(initializeNewForm, transactionId, initNewFormData);
        perf.mark(data, perf.markNames.initializeNewFormEnd);
        yield put(transactionActions.clearSheetAnnouncements({ transactionId }));

        console.log(`handleDeskAssignedMessage(): Getting relays, transactionId=${transactionId}`);
        perf.mark(data, perf.markNames.fetchRelaysStart);
        yield call(getRelaysConfig, transactionId, gateId);
        perf.mark(data, perf.markNames.fetchRelaysEnd);

        /**
         * ANNOUNCEMENTS and HISTORY DATA FETCHING STRATEGY:
         *
         * SPLIT SCREEN VIEW MODE:
         * - Table data (history, manual announcements) is NOT pre-fetched here
         * - Data is loaded LAZILY when user clicks on topic button (History, Manual, etc.)
         * - Lazy loading is handled by useExpandedPanelData hook in ExpandedTopicPanel component
         * - This improves performance by avoiding unnecessary API calls for all transactions
         *
         * CLASSIC/OLD TRANSACTION VIEW MODE:
         * - Table data is PRE-FETCHED immediately when transaction is assigned to desk
         * - This was the ORIGINAL behavior - all data for ALL transactions was fetched upfront
         * - Data is fetched here so that History/Manual tables are ready when user opens transaction tab
         * - fetchLocationAnnouncements flag fetches manual announcements for OUT direction gates
         */
        const isTransactionsSplitViewModeEnabled = yield select(transactionSelectors.selectTransactionsSplitViewMode);
        if (!isTransactionsSplitViewModeEnabled) {
            yield put(
                tableControlActions.fetchTableDataStart({
                    tableId: locationId,
                    locationId,
                    pageNum: 0,
                    fetchLocationAnnouncements: data.gate_direction === 'OUT',
                })
            );
        }

        console.log(`handleDeskAssignedMessage(): Initialized new transaction for transactionId=${transactionId}`);
    } else if (version > currentVersion && automation) {
        console.log(`handleDeskAssignedMessage(): Updating transaction, transactionId=${transactionId}`);
        perf.mark(data, perf.markNames.updateTransactionStart);
        yield call(updateTransaction, transactionId);
        perf.mark(data, perf.markNames.updateTransactionEnd);
        perf.mark(data, perf.markNames.updateCamerasStart);
        yield call(updateTransactionCameras, [...data.cameras.level1, ...data.cameras.level2]);
        perf.mark(data, perf.markNames.updateCamerasEnd);
    } else if (currentVersion && version === 0) {
        console.log(`handleDeskAssignedMessage(): Received base transaction version with delay, updating camera streams....`);
        perf.mark(data, perf.markNames.updateCamerasStart);
        yield call(updateTransactionCameras, [...data.cameras.level1, ...data.cameras.level2]);
        perf.mark(data, perf.markNames.updateCamerasEnd);
    } else {
        currentTransaction &&
            console.log(
                `handleDeskAssignedMessage(): Current transaction is up to date. Aborting! currentVersion=${currentVersion}, incomingVersion=${version}`
            );

        !currentTransaction &&
            console.log(`handleDeskAssignedMessage(): Transaction with transactionId=${transactionId} is finished and removed from state. Aborting!`);
    }

    const isMarkedForDropping = transactionIdsMarkedForDropping.includes(transactionId);
    if (!stompBrokerTopics[stompTopics.transactionDesk] || isMarkedForDropping) {
        console.log(
            `handleDeskAssignedMessage(): ${
                isMarkedForDropping ? 'Transaction marked for dropping' : 'Desk is no longer active'
            }, cleaning up state after initiation of transactionId=${transactionId}`
        );
        const initializedTransaction = yield select(transactionSelectors.selectTranasctionById(transactionId));

        if (initializedTransaction) {
            const { id: transactionId, locationId, tabId } = initializedTransaction;
            yield put(
                transactionActions.closeTransactionPanel({
                    transactionId,
                    locationId,
                    tabId,
                    closeAllPanels: false, //cleaning only state related to this specific transaction
                    closeKurentoClient: false,
                    isTransactionPageActive: true,
                    // An authoritative AIQ notification took the transaction away from us
                    // ("marked for dropping" / "desk is no longer active"), which is the only
                    // thing that justifies `completed_by_other` - never a guess made because a
                    // panel vanished.
                    activityEndReason: ACTIVITY_END_REASON.COMPLETED_BY_OTHER,
                })
            );
        } else {
            yield put(formControlActions.removeForm(transactionId));
        }
    }
}

export function* getRelaysConfig(transactionId, gateId) {
    try {
        console.log(`getRelayConfig(): Requesting relay config for transactionId=${transactionId}, gateId=${gateId}`);
        const response = yield axiosAiq(`/relay/${gateId}`);
        // BE returns a bare array of RelayDTOs; the gate's automaticPulse and relayEnforcement are
        // both denormalized onto every entry (see RelayService#getRelaysForGate), so any entry carries
        // the lane's value.
        // automaticPulse is a 'NONE' | 'SUBMIT' | 'ALL' enum; default to the safest disabled state.
        const relaysConfig = response.data || [];
        const automaticPulse = relaysConfig[0]?.automaticPulse || 'NONE';
        const relayEnforcement = resolveRelayEnforcementFromRelaysConfig(relaysConfig);
        yield put(formControlActions.setRelaysConfig({ transactionId, relaysConfig }));
        yield put(formControlActions.setAutomaticPulse({ transactionId, automaticPulse }));
        yield put(formControlActions.setRelayEnforcement({ transactionId, relayEnforcement }));
    } catch (err) {
        console.log(`getRelayConfig(): Error getting relay config for transactionId=${transactionId}, gateId=${gateId}, message=${err?.message}`);
        yield call(notificationHandler, {
            err,
            title: 'Error getting relay config',
        });
    }
}

export function* leaveDesk(unsubscribe = true) {
    resetStickyLaneHydration();
    resetLaneAot();
    yield put(desksActions.setCurrentDesk({}));

    if (unsubscribe) {
        yield put(transactionActions.unsubscribeFromStompBrokerTopic(stompTopics.transactionDesk));
    }
    yield put(desksActions.setIsActiveDeskConfirmed(true));
    yield put(
        transactionActions.closeTransactionPanel({
            closeAllPanels: true,
            closeKurentoClient: false,
            // The agent stepped away from the work, so this is a context switch and not a
            // completion of any kind.
            activityEndReason: ACTIVITY_END_REASON.CONTEXT_SWITCH,
        })
    );

    sessionStorage.removeItem('desk');
}

// Macaw images of a changed transaction arrive through TransactionImageStatusMessage, no images fetch here
export function* updateTransaction(transactionId) {
    yield put(transactionActions.setUpdatingTransactionInProgress({ isUpdateInProgress: true, transactionId }));
    yield call(updateTransactionDataAutomation, transactionId);
    yield put(transactionActions.setUpdatingTransactionInProgress({ isUpdateInProgress: false, transactionId }));
}

export function* handleDeskAotMessage(data) {
    const camConfigList = data.cameras;
    const camIds = new Set(camConfigList.map((camConfig) => camConfig.id));
    console.log(`handleStompBrokerMessage(): DeskAotMessage received, operation=${data.op} camIds=[${[...camIds]}]`);
    if (data.op === 'DETECT') {
        // cameras an open transaction plays as SUB keep playing it (transactionSubStreamConfigEnabled)
        const subStreamCameraIds = yield select(selectSubStreamCameraIds);
        camConfigList.forEach((camConfig) => {
            const config = transformCameraConfiguration(camConfig);
            cameraStreamManager.activateAot(config, { keepActiveStreamType: subStreamCameraIds.has(config.camId) });
        });
    } else if (data.op === 'CANCEL') {
        camConfigList.forEach((camConfig) => cameraStreamManager.cancelAot(transformCameraConfiguration(camConfig)));
    }
}

export function* updateTransactionCameras(cameraList) {
    // same SUB guard as for a new transaction: a SUB in another pipeline would move the camera's connection off MAIN's.
    // A camera whose SUB is dropped here stops being a SUB camera, so its tiles go back to MAIN.
    const usableCameras = withUsableSubStreams(cameraList);
    if (usableCameras !== cameraList) {
        const droppedSubCameraIds = cameraList.filter((camera) => !usableCameras.includes(camera)).map((camera) => camera.camera_id ?? camera.id);
        yield put(transactionActions.dropCameraSubStreams(droppedSubCameraIds));
    }
    for (let camera of usableCameras) {
        if (camera?.player_id) {
            const transactionIdsArr = cameraStreamManager.getTransactionIdsByCameraId(camera.id);
            console.log(`updateTransactionCameras(): updating cameraId=${camera.id}`);
            yield fork(updateCameraConfig, camera, transactionIdsArr);
        }
    }
}

function* updateTransactionDataAutomation(transactionId) {
    try {
        const response = yield axiosAiq.get(`/transactions/trucks/${transactionId}/automation`);
        const { data: automation } = response;
        const version = automation?.version;
        removeByNestedValue(automation, 'UNKNOWN');
        yield put(transactionActions.updateTransactionVersion({ transactionId, version }));
        const isSplitViewMode = yield select(transactionSelectors.selectTransactionsSplitViewMode);
        yield put(formControlActions.updateTransactionDataAutomation({ automation, isSplitViewMode }));
        console.log(
            `updateTransactionDataAutomation(): Successfuly updated transaction data for transactionId=${transactionId} to version=${version}`
        );
    } catch (err) {
        console.log(`updateTransactionDataAutomation(): Error updating transaction data for transactionId=${transactionId}, message=${err.message}`);
        yield call(notificationHandler, {
            err,
            title: 'Error updating transaction data automation',
        });
    }
}

/**
 * Loads all Macaw images of a transaction: S3 links for uploaded images, status only for the others.
 * Called when the transaction is opened, after a websocket reconnect and when a link expired
 * (`refreshUrls`). Later status changes arrive through TransactionImageStatusMessage.
 */
export function* getTransactionEncodedImages(transactionId, { refreshUrls = false } = {}) {
    perf.mark(transactionId, perf.markNames.fetchEncodedImagesStart);
    try {
        console.log(`getTransactionEncodedImages(): Requesting Macaw images, transactionId=${transactionId}, refreshUrls=${refreshUrls}`);
        const imageName = `Macaw ${getNowFormattedDate()}`;
        const encodedImages = yield call(getTransactionImages, transactionId);
        console.log(`getTransactionEncodedImages(): Received Macaw images, transactionId=${transactionId}`);
        if (!encodedImages?.length) {
            perf.mark(transactionId, perf.markNames.fetchEncodedImagesEnd);
            return;
        }
        yield put(formControlActions.setEncodedImages({ encodedImages, transactionId, imageName, refreshUrls }));
        yield call(trackMacawUploads, transactionId, encodedImages, imageName);

        console.log(`getTransactionEncodedImages(): Successfuly updated Macaw images for transactionId=${transactionId}`);
    } catch (err) {
        console.log(`getTransactionEncodedImages(): Error updating Macaw images for transactionId=${transactionId}, message=${err.message}`);
        yield call(notificationHandler, {
            err,
            title: 'Error getting transaction images',
        });
    }

    perf.mark(transactionId, perf.markNames.fetchEncodedImagesEnd);
}

// upload indicator: kept per transaction also after submit / discard, when the form (and its images) is gone
function* trackMacawUploads(transactionId, images, imageName) {
    const transaction = yield select(transactionSelectors.selectTranasctionById(transactionId));
    yield put(
        transactionImageActions.macawImagesStatusChanged({
            transactionId,
            locationId: transaction?.locationId,
            locationName: transaction?.locationName,
            name: imageName,
            images,
        })
    );
}

function* refreshOpenTransactionsImages() {
    const forms = yield select(selectFormsObject);
    for (const transactionId of Object.keys(forms || {})) {
        yield fork(getTransactionEncodedImages, Number(transactionId));
    }
}

// several images of a transaction are usually uploaded at the same moment: one store update for all of them
const IMAGE_STATUS_COALESCE_MS = 100;

export function* onTransactionImageStatusReceived() {
    const channel = yield actionChannel(transactionActions.transactionImageStatusReceived.type, buffers.expanding(20));

    while (true) {
        const first = yield take(channel);
        yield delay(IMAGE_STATUS_COALESCE_MS);
        const rest = yield flush(channel);

        const imagesByTransactionId = new Map();
        [first, ...rest].forEach(({ payload }) => {
            const transactionId = payload?.truck_transaction_id;
            if (!transactionId || !payload?.image) return;
            if (!imagesByTransactionId.has(transactionId)) imagesByTransactionId.set(transactionId, []);
            imagesByTransactionId.get(transactionId).push(payload.image);
        });

        const imageName = `Macaw ${getNowFormattedDate()}`;
        for (const [transactionId, encodedImages] of imagesByTransactionId) {
            // an error here would end this loop and the root saga with it: the next messages are handled anyway
            try {
                console.log(`onTransactionImageStatusReceived(): ${encodedImages.length} Macaw image status(es) for transactionId=${transactionId}`);
                yield put(formControlActions.setEncodedImages({ encodedImages, transactionId, imageName }));
                yield call(trackMacawUploads, transactionId, encodedImages, imageName);

                // the backend sends no link once the transaction is closed; a form still open on the desk (closed by
                // the discard job or by someone else) gets the links of those images from the images request
                if (encodedImages.some((image) => image.status === MACAW_IMAGE_STATUS.READY && !image.url)) {
                    const forms = yield select(selectFormsObject);
                    if (forms?.[transactionId]) yield fork(getTransactionEncodedImages, transactionId);
                }
            } catch (err) {
                console.error(
                    `onTransactionImageStatusReceived(): transactionId=${transactionId} - error updating Macaw images, message=${err?.message}`
                );
            }
        }
    }
}

const refreshingImagesTransactionIds = new Set();

function* refreshTransactionImages({ payload: { transactionId } }) {
    // several expired links of one transaction fail at the same time: one request is enough
    if (refreshingImagesTransactionIds.has(transactionId)) return;
    refreshingImagesTransactionIds.add(transactionId);
    try {
        yield call(getTransactionEncodedImages, transactionId, { refreshUrls: true });
    } finally {
        refreshingImagesTransactionIds.delete(transactionId);
    }
}

export function* onRefreshTransactionImages() {
    yield takeEvery(transactionActions.refreshTransactionImages.type, refreshTransactionImages);
}

function* handleWebSocketError() {
    yield put(
        snackbarActions.enqueueSnackbar({
            message: `Stomp Broker connection problem detected! Initiating reconnect...`,
            options: {
                variant: 'warning',
            },
        })
    );
}

function* handleError() {
    yield put(desksActions.setCurrentDeskInactive());
    yield put(desksActions.setCurrentDesk({}));
    yield put(
        transactionActions.closeTransactionPanel({
            closeAllPanels: true,
            closeKurentoClient: false,
            // The stomp broker disconnected: an infrastructure failure, not an agent action and
            // not a completion. Never drop an event because it cannot be classified - emit
            // `unknown`.
            activityEndReason: ACTIVITY_END_REASON.UNKNOWN,
        })
    );
    yield put(transactionActions.setActiveTab(desksTabId));
    yield call(notificationHandler, {
        title: `Stomp Broker disconnected, try to reload the page`,
    });
}

export function* subscribeToStompBrokerTopic({ payload: { topic: topicName, url: topicUrl } }) {
    yield sharedWorkerActions.subscribe({ topicName, topicUrl });
    stompBrokerTopics[topicName] = topicUrl;
}

export function* unsubscribeFromStompBrokerTopic({ payload: topicName }) {
    if (stompBrokerTopics[topicName]) {
        yield sharedWorkerActions.unsubscribe({ topicName, url: stompBrokerTopics[topicName] });
        stompBrokerTopics[topicName] = false;
    }
}

export function* closeTransactionPanel({
    payload: {
        transactionId,
        locationId,
        tabId,
        closeAllPanels = false,
        closeKurentoClient = true,
        isTransactionPageActive = true,
        queueFinishedTransaction = true,
    },
}) {
    if (closeAllPanels) {
        transactionIdsMarkedForDropping = [];
        const activeTab = isTransactionPageActive ? desksTabId : 0;
        yield put(transactionActions.setActiveTab(activeTab));
        const transacions = yield select(transactionSelectors.selectTransactions);
        for (let transacion of transacions) yield put(formControlActions.removeForm(transacion.id));
        yield put(transactionActions.setTransactions([]));
        yield put(transactionActions.setUpdatingTransactionInProgress({ initialState: true }));
        yield put(transactionActions.setHasReopenedTransaction(false));
        yield put(tableControlActions.setAllTablesData({}));
        yield put(transactionActions.removeVideoWallCameras());
        yield put(transactionActions.clearSplitViewActiveTransaction());
        yield call([transactionManager, transactionManager.closeAll]);
        yield cameraStreamManager.stopAllStreams(closeKurentoClient);
        yield talkManager.stopSession();
    } else {
        const forms = yield select(selectFormsObject);
        const transactions = yield select(selectTransactions);
        const activeTalkUrl = yield select(transactionSelectors.selectActiveTalkUrl);
        const oldLaneArrivalOrder = yield select(transactionSelectors.selectLaneArrivalOrder);

        transactionIdsMarkedForDropping = transactionIdsMarkedForDropping.filter((id) => id !== transactionId);

        if (queueFinishedTransaction) {
            finishedTransactionIdsQueue.unshift(transactionId);
            setTimeout(() => finishedTransactionIdsQueue.pop(), yield select(selectExpectedFinishedTransactionMillis));
        }

        let locationTransactionsInUse = false;
        let stopTalkManagerSession = false;

        const currentTab = yield select(transactionSelectors.selectActiveTab);
        const filteredTransactions = transactions.filter((transaction) => {
            if (transaction.id !== transactionId && transaction.locationId === locationId) locationTransactionsInUse = true;

            if (transaction.id === transactionId && transaction.camsConfig.talkUrls.includes(activeTalkUrl)) stopTalkManagerSession = true;
            return transaction.id !== transactionId;
        });

        yield put(transactionActions.setTransactions(filteredTransactions));
        yield put(transactionActions.setUpdatingTransactionInProgress({ isUpdateInProgress: false, transactionId }));
        yield put(transactionActions.setHasReopenedTransaction(!!filteredTransactions.find(({ tabId }) => tabId === reopenedTransactionTabId)));
        const isVideoWallEnabled = yield select(transactionSelectors.selectIsVideoWallEnabled);
        const currentDesk = yield select(selectCurrentDesk);

        if (currentTab === tabId) {
            const newActiveTab = filteredTransactions.length
                ? filteredTransactions[0].tabId
                : currentDesk.id && isVideoWallEnabled
                  ? videoWallTabId
                  : desksTabId;
            yield put(transactionActions.setActiveTab(newActiveTab));
        }
        /* eslint-disable-next-line no-unused-vars */
        const { [transactionId]: removed, ...filteredForms } = forms;
        const hasActiveTransactionOnSameLocation = Object.values(filteredForms).some(
            ({ transactionData }) => transactionData?.gate?.location?.id === locationId
        );
        if (!hasActiveTransactionOnSameLocation) {
            yield put(formControlActions.setInventoryData({ inventoryData: null, locationId }));
            yield put(formControlActions.setEquipmentData({ equipmentData: null, locationId }));
        }

        yield put(formControlActions.setFormsObj(filteredForms));

        if (!locationTransactionsInUse) {
            const locationTransactions = yield select(selectAllTablesData);
            /* eslint-disable no-unused-vars */
            const {
                [locationId]: removed,
                [`${locationId}_manualAnnouncements`]: announcements,
                ...filteredLocationTransactions
            } = locationTransactions;
            yield put(tableControlActions.setAllTablesData(filteredLocationTransactions));
        }

        yield call([transactionManager, transactionManager.closeTransactionPipeline], transactionId);
        if (stopTalkManagerSession) yield talkManager.stopSession();
        const isTransactionsSplitViewModeEnabled = yield select(transactionSelectors.selectTransactionsSplitViewMode);
        if (!isTransactionsSplitViewModeEnabled) yield cameraStreamManager.closeTab(tabId, transactionId);

        if (isTransactionsSplitViewModeEnabled) {
            const activeSplitViewTxnId = yield select(transactionSelectors.selectSplitViewActiveTransactionId);
            if (activeSplitViewTxnId === transactionId) {
                if (filteredTransactions.length === 0) {
                    yield put(transactionActions.clearSplitViewActiveTransaction());
                } else {
                    const closedTxn = transactions.find((t) => t.id === transactionId);
                    const closedGroupKey = getGroupKey(closedTxn);

                    const sameGroupTxns = filteredTransactions.filter((t) => getGroupKey(t) === closedGroupKey);

                    if (sameGroupTxns.length > 0) {
                        const nextTxn = sameGroupTxns[0];
                        yield put(transactionActions.setSplitViewActiveTransaction({ transactionId: nextTxn.id, transactionGroup: closedGroupKey }));
                    } else {
                        const groupedTransactions = yield select(transactionSelectors.selectGroupedTransactions);
                        const closedLaneIndex = oldLaneArrivalOrder.indexOf(closedGroupKey);
                        const nextGroupIndex = Math.min(closedLaneIndex, groupedTransactions.length - 1);
                        const nextGroup = groupedTransactions[Math.max(nextGroupIndex, 0)];
                        const nextTxn = nextGroup.transactions[0];
                        yield put(transactionActions.setSplitViewActiveTransaction({ transactionId: nextTxn.id, transactionGroup: nextGroup.key }));
                    }
                }
            }
        }

        console.log(`closeTransactionPanel(): transactionId=${transactionId} successfully removed from state`);
    }
}

export function* setActiveTalkUrl({ payload: { activeTalkUrl, sendSignal = true } }) {
    yield sessionStorage.setItem('talkUrl', JSON.stringify(activeTalkUrl));
    sendSignal && sendLocalStorageSignal('talkUrlChanged', activeTalkUrl);
}

export function* setActiveDemoTalkUrl({ payload: { talkUrl, sendSignal = true } }) {
    yield sessionStorage.setItem('demoTalkUrl', JSON.stringify(talkUrl));
    sendSignal && sendLocalStorageSignal('demoTalkUrlChanged', talkUrl);
}

function* registerSharedWorker() {
    const worker = getSharedWorker();
    const { username } = yield select(selectUser);
    const transactionModeOn = yield select(transactionSelectors.selectIsReceivingTransactionsEnabled);
    const videoWallModeOn = yield select(transactionSelectors.selectIsVideoWallEnabled);
    const subscriptions = [];
    transactionModeOn && subscriptions.push(stompTopics.transactionDesk);
    videoWallModeOn && subscriptions.push(stompTopics.videoWall);
    worker.port.start();
    messageType.sharedWorkerPort = worker.port;
    sharedWorkerObj.port = worker.port;
    worker.onerror = (e) => console.error(`SharedWorker error: message=${e?.message}`);
    sharedWorkerActions.registerWindow({ username, subscriptions });
    startSharedWorkerHeartbeat(worker.port);
    yield call(setSharedWorkerListener, worker.port);
}

function* setSharedWorkerListener(port) {
    const channel = yield call(createMessageChanel, port);
    let orphanCleanupTask = null;
    while (channel) {
        try {
            const payload = yield take(channel);
            const { type, data } = payload;
            switch (type) {
                case messageType.registerWindow:
                    aiqLogger.setWindowUid(data.uid);
                // eslint-disable-next-line
                case messageType.windowIdUpdate:
                    yield put(transactionActions.setWindowId(data));
                    sharedWorkerObj.id = data.id;
                    break;
                case messageType.stompMessage:
                    yield fork(handleStompBrokerResponse, data);
                    break;
                case messageType.deskSelected:
                    {
                        const { id: currentDeskId } = yield select(selectCurrentDesk);
                        if (currentDeskId !== data.id) yield put(desksActions.selectDesk(data));
                    }
                    break;
                case messageType.windowsCountChange:
                    yield put(transactionActions.setWindowCount(data));
                    break;
                case messageType.pinnedLanesChange:
                    yield put(transactionActions.setPinnedLanes(data));
                    break;
                case messageType.streamingCameraTotalChange:
                    yield put(transactionActions.setStreamingCameraTotal(data));
                    break;
                case messageType.videoWallsChange:
                    yield put(transactionActions.setVideoWalls(data));
                    break;
                case messageType.applyVideoWallConfig:
                    yield call(applyDisplayStatesToWall, data);
                    break;
                case messageType.transactionFinished:
                    onCloseTransaction(data);
                    break;
                case messageType.deskPauseRequestSent:
                    yield put(desksActions.setPauseRequestSent(data));
                    break;
                case messageType.machineWakeUpDetected:
                    cameraStreamManager.restartAll();
                    break;
                case messageType.releaseOrphanedEndpoints:
                    if (orphanCleanupTask?.isRunning()) yield cancel(orphanCleanupTask);
                    orphanCleanupTask = yield fork(releaseOrphanedEndpointsSaga, data);
                    break;
                case messageType.forceReload:
                    window.location.reload();
                    break;
                case messageType.stompBrokerConnectionStateChange:
                    yield fork(handleStompConnectionStateChange, data);
                    break;
                default:
            }
        } catch (err) {
            console.error(err);
        }
    }
}

function onCloseTransaction(transactionIds) {
    transactionIds.forEach((transactionId) => {
        transactionIdsMarkedForDropping.push(transactionId);
        const transaction = store.getState().transactions.transactions.find((transaction) => transaction.id === transactionId);
        if (transaction) {
            const { tabId, locationId } = transaction;
            store.dispatch(
                transactionActions.closeTransactionPanel({
                    tabId,
                    transactionId,
                    locationId,
                    queueFinishedTransaction: false,
                    // THE TRAP. This looks external because it arrives as a shared-worker message,
                    // but it is the SAME agent finishing the transaction in another browser
                    // window, and the message is only broadcast after success. Reading it as
                    // `completed_by_other` would put a false "someone took my work" fact into an
                    // immutable store for every multi-window agent, which is most of them.
                    activityEndReason: ACTIVITY_END_REASON.COMPLETED_BY_SELF,
                })
            );
            store.dispatch(
                tableControlActions.fetchTableDataStart({
                    tableId: locationId,
                    locationId,
                    pageNum: 0,
                })
            );
        }
    });
}

export function* releaseOrphanedEndpointsSaga({ orphanedTabIds }) {
    try {
        if (!orphanedTabIds?.length) return;
        const allRecords = yield call([webRtcEndpointRegistry, 'getAll']);
        if (!allRecords.length) return;

        const orphanedTabIdSet = new Set(orphanedTabIds);
        const orphanedRecords = allRecords.filter(({ tabId }) => tabId && orphanedTabIdSet.has(tabId));
        if (!orphanedRecords.length) return;

        for (let i = 0; i < orphanedRecords.length; i++) {
            const { webrtcEndpointId, kurentoId } = orphanedRecords[i] || {};
            if (!webrtcEndpointId || !kurentoId) continue;

            try {
                yield call([kurentoClientManager, 'releaseMediaObject'], kurentoId, webrtcEndpointId);
                yield call([webRtcEndpointRegistry, 'remove'], webrtcEndpointId);
            } catch (err) {
                if (err.code === 40101 && err?.message?.includes(webrtcEndpointId)) {
                    console.log(
                        `releaseOrphanedEndpointsSaga(): WebRtcEndpoint id=${webrtcEndpointId} was not found on kurentoId=${kurentoId}, removing from registry...`
                    );
                    yield call([webRtcEndpointRegistry, 'remove'], webrtcEndpointId);
                } else {
                    console.error(`releaseOrphanedEndpointsSaga(): failed to release endpoint id=${webrtcEndpointId} kurentoId=${kurentoId}`, err);
                    const updated = yield call([webRtcEndpointRegistry, 'incrementReleaseErrorCount'], webrtcEndpointId);
                    if (updated?.releaseErrorCount > 3 && updated?.createdAt) {
                        const ageMs = Date.now() - new Date(updated.createdAt).getTime();
                        if (ageMs > 8 * 60 * 60 * 1000) {
                            console.log(
                                `releaseOrphanedEndpointsSaga(): endpoint id=${webrtcEndpointId} exceeded error threshold and is older than 8h, removing from registry...`
                            );
                            yield call([webRtcEndpointRegistry, 'remove'], webrtcEndpointId);
                        }
                    }
                }
            }

            if (i < orphanedRecords.length - 1) {
                if (!window.relaseOrphansDelayDisabled) {
                    // TODO remove this condition after QA tests are finished
                    yield delay(3000 + Math.floor(Math.random() * 2000));
                }
            }
        }
    } catch (err) {
        console.error('releaseOrphanedEndpointsSaga(): failed to release orphaned endpoints', err);
    }
}

async function createMessageChanel(port) {
    return eventChannel((emmiter) => {
        port.addEventListener('message', (e) => emmiter(e.data));
        return () => {};
    });
}

export function* onGetVideoWallCameras() {
    yield takeLatest(transactionActions.getVideoWallCameras.type, getVideoWallCameras);
}

export function* onVideoWallNotificationTimedOut() {
    yield takeEvery(transactionActions.videoWallNotificationTimedOut.type, handleVideoWallNotificationTimeout);
}

export function* forgetFinishedVideoWallTransactionLater({ payload: truckTransactionId }) {
    // 0 until the desk configuration has loaded, which would forget the transaction right away
    const keepMs = (yield select(selectExpectedFinishedTransactionMillis)) || VIDEO_WALL_FINISHED_TRANSACTION_MEMORY_MS;
    yield delay(keepMs);
    yield put(transactionActions.forgetFinishedVideoWallTransaction(truckTransactionId));
}

export function* onRememberFinishedVideoWallTransaction() {
    yield takeEvery(transactionActions.rememberFinishedVideoWallTransaction.type, forgetFinishedVideoWallTransactionLater);
}

export function* onCloseTransactionPanel() {
    yield takeLatest(transactionActions.closeTransactionPanel.type, closeTransactionPanel);
}

export function* onUnsubscribeFromStompBrokerTopic() {
    yield takeEvery(transactionActions.unsubscribeFromStompBrokerTopic.type, unsubscribeFromStompBrokerTopic);
}

export function* onSubscribeToStompBrokerTopic() {
    yield takeEvery(transactionActions.subscribeToStompBrokerTopic.type, subscribeToStompBrokerTopic);
}

export function* onSetActiveTalkUrl() {
    yield takeEvery(transactionActions.setActiveTalkUrl.type, setActiveTalkUrl);
}

export function* onSetActiveDemoTalkUrl() {
    yield takeEvery(transactionActions.setActiveDemoTalkUrl.type, setActiveDemoTalkUrl);
}

export function* onHandleDeskAssignedMessage() {
    const requestChan = yield actionChannel(transactionActions.deskAssignedMessageReceived.type, new ChannelBuffer()); //

    while (true) {
        const { payload } = yield take(requestChan);
        const transactionId = resolveTransactionId(payload);
        const isMarkedForDropping = transactionIdsMarkedForDropping.includes(transactionId);
        const isReceivingTransactionsEnabled = yield select(transactionSelectors.selectIsReceivingTransactionsEnabled);

        if (isReceivingTransactionsEnabled && stompBrokerTopics[stompTopics.transactionDesk] && !isMarkedForDropping) {
            yield call([transactionManager, transactionManager.processTransaction], transactionId, payload);
        } else {
            const message = isMarkedForDropping ? 'transaction marked for dropping' : 'receiving of transactions is disabled';
            console.log(`onHandleDeskAssignedMessage(): Dropping transaction id=${payload?.truck_transaction_id}, ${message}.`);
        }
    }
}

export function* onToggleVideoWallEnabled() {
    yield takeEvery(transactionActions.toggleVideoWallEnabled.type, toggleVideoWallEnabled);
}

export function* onToggleIsReceivingTransactionsEnabled() {
    yield takeEvery(transactionActions.toggleIsReceivingTransactionsEnabled.type, toggleIsReceivingTransactionsEnabled);
}

export function* onRegisterSharedWorker() {
    yield takeEvery(transactionActions.registerSharedWorker.type, registerSharedWorker);
}
function* applyDisplayStatesToWall(config) {
    const videoWallCameras = yield select(transactionSelectors.selectVideoWallCameras);
    const level1 = videoWallCameras?.level1 || [];
    if (!level1.length) return;
    const currentCollapsed = yield select(transactionSelectors.selectVideoWallCollapsedCameraIds);
    const currentCollapsedSet = new Set((currentCollapsed || []).map(String));

    const payload = level1.map((camera) => {
        const state = config?.[String(camera.id)];
        return state ? { ...camera, hidden: displayStateToFlags(state).hidden } : camera;
    });
    yield put(transactionActions.setCheckedStateToVideoCameras(payload));

    const collapsedIds = level1
        .filter((camera) => {
            const state = config?.[String(camera.id)];
            return state ? displayStateToFlags(state).collapsed : currentCollapsedSet.has(String(camera.id));
        })
        .map((camera) => String(camera.id));
    yield put(transactionActions.setVideoWallCollapsedCameraIds(collapsedIds));
}

function* publishVideoWallConfig() {
    const isVideoWallEnabled = yield select(transactionSelectors.selectIsVideoWallEnabled);
    if (!isVideoWallEnabled) return;
    const videoWallCameras = yield select(transactionSelectors.selectVideoWallCameras);
    const level1 = videoWallCameras?.level1 || [];
    // Do not claim the handoff until cameras are loaded. If level1 is still empty the
    // handoff stays in sessionStorage so the next debounced call can pick it up once
    // the camera list arrives (avoids losing the config on slow boot).
    if (!level1.length) return;
    const collapsed = yield select(transactionSelectors.selectVideoWallCollapsedCameraIds);
    const collapsedSet = new Set((collapsed || []).map(String));
    const config = {};
    level1.forEach((camera) => {
        const id = String(camera.id);
        config[id] = displayStateFromFlags(camera.hidden, collapsedSet.has(id));
    });
    const handoffId = sessionStorage.getItem(VIDEO_WALL_HANDOFF_STORAGE_KEY) || undefined;
    yield call(sharedWorkerActions.videoWallConfigChange, { config, handoffId });
    // Consume the handoff only now that level1 is populated (guarded above), so the worker
    // has already received the matching queueVideoWallConfig. The worker's #awaitingHandoffPorts
    // map covers the reverse race if this message still arrives first.
    if (handoffId) sessionStorage.removeItem(VIDEO_WALL_HANDOFF_STORAGE_KEY);
}

function* saveMultiWallConfig({ payload }) {
    const walls = payload?.walls || [];
    for (const wall of walls) {
        if (!wall?.uid) continue;
        yield call(sharedWorkerActions.applyVideoWallConfig, { targetUid: wall.uid, config: wall.displayStates || {} });
    }
}

function* queueNewVideoWallConfig({ payload }) {
    yield call(sharedWorkerActions.queueVideoWallConfig, { handoffId: payload?.handoffId, config: payload?.config || {} });
}

export function* onPublishVideoWallConfig() {
    yield debounce(
        VIDEO_WALL_CONFIG_PUBLISH_DEBOUNCE_MS,
        [
            transactionActions.setCheckedStateToVideoCameras.type,
            transactionActions.setVideoWallCollapsedCameraIds.type,
            transactionActions.setVideoWallCameras.type,
            transactionActions.setRealTimeVideoWallCameras.type,
            transactionActions.resetVideoWallToDefault.type,
        ],
        publishVideoWallConfig
    );
}

export function* onSaveMultiWallConfig() {
    yield takeEvery(transactionActions.saveMultiWallConfig.type, saveMultiWallConfig);
}

export function* onQueueNewVideoWallConfig() {
    yield takeEvery(transactionActions.queueNewVideoWallConfig.type, queueNewVideoWallConfig);
}

export function* sendReportedIssues({ payload }) {
    try {
        const response = yield axiosAiq.post(payload.url, payload.data[0]);

        if (response.status === 200) {
            yield call(notificationHandler, {
                title: 'Successfully reported issues',
                variant: 'success',
            });
        }
    } catch (err) {
        var mySubString = err.response.data.error.substring(err.response.data.error.indexOf('[') + 1, err.response.data.error.indexOf(']'));
        yield call(notificationHandler, {
            title: `Not able to report issues, ${mySubString ? mySubString : err.response.data.error}`,
        });
    }
}

export function* onSendReportedIssues() {
    yield takeEvery(transactionActions.reportIssues.type, sendReportedIssues);
}
